import React from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Mutation } from '@apollo/client/react/components';

import SVGIcon from 'lib/images/svg-pure-icon/svg-pure-icon';
import FormattedDateAgoView from 'lib/intl/formatted-date-ago-view';

import like from 'four-nets/icons/svg/icon_like-2.svg';
import rightArrow from 'four-nets/icons/svg/icon_arrow-right.svg';
import VendorBadge from 'four-nets/ui-v2/badges/vendor-badge/vendor-badge';
import Avatar from 'four-nets/avatar/avatar';
import { displayedName } from 'four-nets/user/utils';
import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';

import likeMutation, { likePhotoBlogMutation, likePhotoBlogMutationVariables } from './mutations/like.graphql';
import unlikeMutation, { unlikePhotoBlogMutation, unlikePhotoBlogMutationVariables } from './mutations/unlike.graphql';
import { AuthorBottomFragment } from './author-bottom.graphql';
import defaultStyles from './author-bottom.scss';

export interface Props extends AuthorBottomFragment {
    styles: typeof defaultStyles;
}

class AuthorBottom extends React.PureComponent<Props> {
    static contextTypes = {
        authCheck: PropTypes.func,
    };
    render() {
        const { styles, user } = this.props;
        if (!user) {
            return null;
        }

        const { photoBlog, dateJoined, profile } = user;
        const { likesCount, id, isLikedByViewer } = photoBlog!;
        const userBlogLink =
            profile && profile.isVendor ? `/catalog/vendor/${user.username}` : `/blog/${user.username}`;
        const username =
            profile && profile.isVendor && profile.vendor && profile.vendor.name
                ? profile.vendor.name
                : displayedName(user!);

        return user && id ? (
            <div className={styles.wrapper}>
                <div className={styles.top}>
                    <Avatar style={{ avatar: styles.avatar }} user={user} />
                    <div>
                        <div className={styles.titleDiv}>
                            <Link to={userBlogLink} className={styles.userLink}>
                                {username}
                            </Link>
                            {profile && profile.isVendor ? (
                                <div className={styles.badgeDiv}>
                                    <VendorBadge />
                                </div>
                            ) : null}
                        </div>
                        <div className={styles.date}>
                            <FormattedMessage id='MEMBER_SINCE' />
                            &nbsp;
                            <FormattedDateAgoView value={new Date(dateJoined)} />
                        </div>
                        <div className={styles.buttons}>
                            <div className={styles.count}>
                                <SVGIcon src={like} className={styles.icon} />
                                <span className={styles.countPart}>
                                    {(user.photoBlog && user.photoBlog.likesCount) || 0}
                                </span>
                            </div>
                            <Mutation<likePhotoBlogMutation, likePhotoBlogMutationVariables>
                                mutation={likeMutation}
                                optimisticResponse={{
                                    likePhotoBlog: {
                                        __typename: 'LikePhotoBlogPayload',
                                        photoBlog: {
                                            __typename: 'PhotoBlogNode',
                                            id,
                                            isLikedByViewer: true,
                                            likesCount: (likesCount || 0) + 1,
                                        },
                                    },
                                }}
                            >
                                {likePhotoBlog => (
                                    <Mutation<unlikePhotoBlogMutation, unlikePhotoBlogMutationVariables>
                                        mutation={unlikeMutation}
                                        optimisticResponse={{
                                            unlikePhotoBlog: {
                                                __typename: 'UnlikePhotoBlogPayload',
                                                photoBlog: {
                                                    __typename: 'PhotoBlogNode',
                                                    id,
                                                    isLikedByViewer: false,
                                                    likesCount: (likesCount || 1) - 1,
                                                },
                                            },
                                        }}
                                    >
                                        {unlikePhotoBlog => (
                                            <button
                                                className={styles.button}
                                                onClick={() => {
                                                    this.context.authCheck(() => {
                                                        if (isLikedByViewer) {
                                                            return unlikePhotoBlog({ variables: { id } });
                                                        } else {
                                                            return likePhotoBlog({ variables: { id } });
                                                        }
                                                    }, AuthOrigin.LikeBlogPost);
                                                }}
                                            >
                                                {isLikedByViewer ? (
                                                    <FormattedMessage id='UNLIKE' />
                                                ) : (
                                                    <FormattedMessage id='LIKE' />
                                                )}
                                            </button>
                                        )}
                                    </Mutation>
                                )}
                            </Mutation>
                        </div>
                    </div>
                </div>
                <div className={styles.description}>
                    <span>{(user.profile && user.profile.aboutMe) || ''}</span>
                </div>
                <Link to={userBlogLink} className={styles.profileLink}>
                    <FormattedMessage id='BLOG_USER_PROFILE_TITLE'>
                        {msg => <span className={styles.profileLinkText}>{msg.toLowerCase()}</span>}
                    </FormattedMessage>
                    <SVGIcon src={rightArrow} className={styles.profileLinkImage} />
                </Link>
            </div>
        ) : null;
    }
}

export default Styled(defaultStyles)(AuthorBottom);
